import { useEffect, useState, useRef, useMemo } from 'react';
import type { Socket } from 'socket.io-client';

type BattleEffectType =
  | 'fireball'
  | 'resurrection'
  | 'transformation'
  | 'divine-light'
  | 'sleep'
  | 'magic-missiles'
  | 'poison'
  | 'battle-cry'
  | 'druid-summon'
  | 'bard';

const EFFECT_DURATIONS: Record<BattleEffectType, number> = {
  fireball: 4000,
  resurrection: 5000,
  transformation: 4000,
  'divine-light': 4000,
  sleep: 6500,
  'magic-missiles': 3500,
  poison: 5000,
  'battle-cry': 3500,
  'druid-summon': 5500,
  bard: 6000,
};

interface BattleEffectsOverlayProps {
  socket: Socket | null;
}

// Generate random values once per effect trigger
function generateParticles(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    angle: Math.random() * 360,
    distance: 30 + Math.random() * 40,
    size: 15 + Math.random() * 45,
    delay: Math.random() * 0.6,
    duration: 1.5 + Math.random() * 1.5,
    hue: Math.random(),
  }));
}

function generateSparkles(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 10 + Math.random() * 80,
    delay: Math.random() * 2.5,
    duration: 1.5 + Math.random() * 1.5,
    size: 4 + Math.random() * 10,
    drift: -15 + Math.random() * 30,
  }));
}

function generateRings(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    delay: i * 0.3,
    maxScale: 1.5 + Math.random() * 1.5,
    rotation: Math.random() * 360,
    thickness: 2 + Math.random() * 3,
  }));
}

function generateRays(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    angle: -40 + (80 / (count - 1)) * i,
    width: 3 + Math.random() * 6,
    delay: Math.random() * 0.5,
    opacity: 0.4 + Math.random() * 0.5,
  }));
}

function generateZzz(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 15 + Math.random() * 70,
    delay: i * 0.5 + Math.random() * 0.3,
    size: 3 + Math.random() * 4,
    drift: -20 + Math.random() * 40,
    duration: 2 + Math.random() * 1.5,
  }));
}

function generateMissiles(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    startY: 20 + Math.random() * 60,
    curve: -20 + Math.random() * 40,
    delay: i * 0.4,
    duration: 0.8 + Math.random() * 0.6,
    size: 6 + Math.random() * 8,
  }));
}

function generateNotes(count: number) {
  const symbols = ['♪', '♫', '♩', '♬', '𝅘𝅥𝅮'];
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 10 + Math.random() * 80,
    delay: i * 0.35 + Math.random() * 0.2,
    size: 2.5 + Math.random() * 3.5,
    drift: -25 + Math.random() * 50,
    duration: 2 + Math.random() * 1.5,
    symbol: symbols[i % symbols.length],
    hue: Math.random(),
  }));
}

function generateBubbles(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 5 + Math.random() * 90,
    size: 12 + Math.random() * 35,
    delay: Math.random() * 2.5,
    duration: 2 + Math.random() * 2,
    drift: -20 + Math.random() * 40,
    hue: Math.random(),
  }));
}

function generateDrips(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 3 + Math.random() * 94,
    width: 2 + Math.random() * 4,
    height: 15 + Math.random() * 35,
    delay: Math.random() * 1.5,
  }));
}

function generateShockwaves(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    delay: i * 0.4,
    maxScale: 2 + i * 0.8,
    duration: 1.2 + i * 0.3,
  }));
}

function generateForceLines(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    angle: (360 / count) * i,
    width: 2 + Math.random() * 3,
    delay: Math.random() * 0.4,
    opacity: 0.4 + Math.random() * 0.4,
  }));
}

const CSS_KEYFRAMES = `
  @keyframes effect-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Fireball */
  @keyframes fireball-particle {
    0% {
      transform: translate(-50%, -50%) scale(0);
      opacity: 1;
    }
    20% {
      opacity: 1;
    }
    100% {
      transform: translate(
        calc(-50% + var(--tx)),
        calc(-50% + var(--ty))
      ) scale(1.3);
      opacity: 0;
    }
  }
  @keyframes fireball-flash {
    0% { opacity: 0; transform: scale(0.3); }
    15% { opacity: 0.9; transform: scale(1); }
    100% { opacity: 0; transform: scale(2); }
  }
  @keyframes fireball-core {
    0% { opacity: 0; transform: translate(-50%, -50%) scale(0); }
    10% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.2); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
  }

  /* Resurrection */
  @keyframes resurrection-column {
    0% { transform: scaleY(0); opacity: 0; }
    20% { transform: scaleY(1); opacity: 0.8; }
    70% { transform: scaleY(1); opacity: 0.6; }
    100% { transform: scaleY(1); opacity: 0; }
  }
  @keyframes resurrection-sparkle {
    0% {
      transform: translate(var(--drift), 0) scale(0);
      opacity: 0;
    }
    15% {
      transform: translate(calc(var(--drift) * 0.3), -10vh) scale(1);
      opacity: 1;
    }
    100% {
      transform: translate(calc(var(--drift) * -0.5), -80vh) scale(0.3);
      opacity: 0;
    }
  }

  /* Transformation */
  @keyframes transformation-ring {
    0% {
      transform: translate(-50%, -50%) scale(0) rotate(0deg);
      opacity: 0.8;
    }
    70% {
      opacity: 0.5;
    }
    100% {
      transform: translate(-50%, -50%) scale(var(--max-scale)) rotate(var(--rotation));
      opacity: 0;
    }
  }
  @keyframes transformation-spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
  @keyframes transformation-pulse {
    0% { opacity: 0; }
    30% { opacity: 0.3; }
    70% { opacity: 0.2; }
    100% { opacity: 0; }
  }

  /* Divine Light */
  @keyframes divine-ray {
    0% {
      opacity: 0;
      transform: rotate(var(--angle)) scaleY(0);
    }
    20% {
      opacity: var(--ray-opacity);
      transform: rotate(var(--angle)) scaleY(1);
    }
    70% {
      opacity: var(--ray-opacity);
    }
    100% {
      opacity: 0;
      transform: rotate(var(--angle)) scaleY(1);
    }
  }
  @keyframes divine-flash {
    0% { opacity: 0; }
    8% { opacity: 0.9; }
    25% { opacity: 0.3; }
    100% { opacity: 0; }
  }
  @keyframes divine-halo {
    0% { opacity: 0; transform: translate(-50%, -20%) scale(0.5); }
    20% { opacity: 0.6; transform: translate(-50%, -20%) scale(1); }
    70% { opacity: 0.4; transform: translate(-50%, -20%) scale(1.2); }
    100% { opacity: 0; transform: translate(-50%, -20%) scale(1.5); }
  }

  /* Sleep */
  @keyframes sleep-zzz {
    0% {
      transform: translate(0, 0) scale(0.5) rotate(-10deg);
      opacity: 0;
    }
    15% {
      opacity: 1;
      transform: translate(var(--drift), -5vh) scale(1) rotate(0deg);
    }
    100% {
      transform: translate(calc(var(--drift) * 2), -70vh) scale(1.5) rotate(10deg);
      opacity: 0;
    }
  }
  @keyframes sleep-mist {
    0% { opacity: 0; }
    20% { opacity: 0.25; }
    50% { opacity: 0.15; }
    80% { opacity: 0.25; }
    100% { opacity: 0; }
  }

  /* Magic Missiles (fire arrows) */
  @keyframes missile-streak {
    0% {
      left: -8%;
      top: var(--start-y);
      opacity: 0;
      transform: scale(0.5) rotate(-5deg);
    }
    8% {
      opacity: 1;
      transform: scale(1) rotate(0deg);
    }
    50% {
      top: calc(var(--start-y) + var(--curve));
    }
    90% {
      opacity: 1;
    }
    100% {
      left: 108%;
      top: var(--start-y);
      opacity: 0;
      transform: scale(0.9) rotate(2deg);
    }
  }
  @keyframes missile-flame-flicker {
    0%, 100% { opacity: 0.8; transform: translateY(-50%) scaleY(1); }
    25% { opacity: 1; transform: translateY(-50%) scaleY(1.15); }
    50% { opacity: 0.7; transform: translateY(-50%) scaleY(0.9); }
    75% { opacity: 0.95; transform: translateY(-50%) scaleY(1.1); }
  }

  /* Poison */
  @keyframes poison-bubble {
    0% {
      transform: translateX(var(--drift)) scale(0.3);
      opacity: 0;
    }
    15% {
      opacity: 0.8;
      transform: translateX(calc(var(--drift) * 0.5)) scale(1);
    }
    80% {
      opacity: 0.5;
    }
    100% {
      transform: translateX(calc(var(--drift) * -0.3)) translateY(-75vh) scale(0.6);
      opacity: 0;
    }
  }
  @keyframes poison-mist {
    0% { opacity: 0; }
    15% { opacity: 0.3; }
    60% { opacity: 0.25; }
    100% { opacity: 0; }
  }
  @keyframes poison-drip {
    0% { height: 0; opacity: 0; }
    10% { opacity: 0.7; }
    60% { height: var(--drip-h); opacity: 0.5; }
    100% { height: var(--drip-h); opacity: 0; }
  }

  /* Druid Summon */
  @keyframes summon-circle {
    0% { transform: translate(-50%, -50%) scale(0) rotate(0deg); opacity: 0; }
    20% { opacity: 0.8; transform: translate(-50%, -50%) scale(1) rotate(30deg); }
    70% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1) rotate(180deg); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1.3) rotate(360deg); }
  }
  @keyframes summon-pillar {
    0% { transform: scaleY(0); opacity: 0; }
    15% { opacity: 0.7; transform: scaleY(1); }
    50% { opacity: 0.5; transform: scaleY(1.1); }
    100% { opacity: 0; transform: scaleY(1); }
  }
  @keyframes summon-rune {
    0% { opacity: 0; transform: translate(var(--rx), var(--ry)) scale(0) rotate(0deg); }
    20% { opacity: 0.9; transform: translate(var(--rx), var(--ry)) scale(1) rotate(45deg); }
    60% { opacity: 0.6; transform: translate(var(--rx), var(--ry)) scale(1.1) rotate(90deg); }
    100% { opacity: 0; transform: translate(var(--rx), var(--ry)) scale(0.5) rotate(180deg); }
  }
  @keyframes summon-leaf {
    0% {
      transform: translate(0, 0) rotate(0deg) scale(0);
      opacity: 0;
    }
    15% {
      opacity: 0.8;
      transform: translate(var(--lx1), var(--ly1)) rotate(90deg) scale(1);
    }
    50% {
      transform: translate(var(--lx2), var(--ly2)) rotate(200deg) scale(0.8);
      opacity: 0.6;
    }
    100% {
      transform: translate(var(--lx3), var(--ly3)) rotate(360deg) scale(0.3);
      opacity: 0;
    }
  }

  /* Bard */
  @keyframes bard-note {
    0% {
      transform: translate(0, 0) scale(0.5) rotate(-15deg);
      opacity: 0;
    }
    15% {
      opacity: 1;
      transform: translate(var(--drift), -8vh) scale(1) rotate(0deg);
    }
    60% {
      opacity: 0.8;
      transform: translate(calc(var(--drift) * 1.5), -40vh) scale(1.1) rotate(10deg);
    }
    100% {
      transform: translate(calc(var(--drift) * 2), -75vh) scale(0.7) rotate(20deg);
      opacity: 0;
    }
  }
  @keyframes bard-glow {
    0% { opacity: 0; }
    15% { opacity: 0.2; }
    50% { opacity: 0.15; }
    80% { opacity: 0.2; }
    100% { opacity: 0; }
  }

  /* Battle Cry */
  @keyframes battlecry-ring {
    0% {
      transform: translate(-50%, -50%) scale(0);
      opacity: 0.9;
      border-width: 6px;
    }
    100% {
      transform: translate(-50%, -50%) scale(var(--max-scale));
      opacity: 0;
      border-width: 1px;
    }
  }
  @keyframes battlecry-flash {
    0% { opacity: 0; }
    10% { opacity: 0.5; }
    30% { opacity: 0; }
  }
  @keyframes battlecry-line {
    0% {
      transform: rotate(var(--angle)) scaleX(0);
      opacity: 0;
    }
    20% {
      opacity: var(--line-opacity);
      transform: rotate(var(--angle)) scaleX(1);
    }
    70% {
      opacity: var(--line-opacity);
    }
    100% {
      opacity: 0;
      transform: rotate(var(--angle)) scaleX(1.2);
    }
  }
`;

function FireballEffect() {
  const particles = useMemo(() => generateParticles(25), []);
  return (
    <>
      {/* Central flash */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: '60vmin',
          height: '60vmin',
          marginLeft: '-30vmin',
          marginTop: '-30vmin',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,150,0,0.9), rgba(255,60,0,0.4), transparent 70%)',
          animation: 'fireball-flash 3s ease-out forwards',
        }}
      />
      {/* Core */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: '15vmin',
          height: '15vmin',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #fff 0%, #ffcc00 30%, #ff6600 60%, transparent 100%)',
          animation: 'fireball-core 3s ease-out forwards',
        }}
      />
      {/* Particles */}
      {particles.map((p) => {
        const rad = (p.angle * Math.PI) / 180;
        const tx = Math.cos(rad) * p.distance;
        const ty = Math.sin(rad) * p.distance;
        const color = p.hue < 0.3 ? '#ff4400' : p.hue < 0.6 ? '#ff8800' : '#ffcc00';
        return (
          <div
            key={p.id}
            className="absolute left-1/2 top-1/2"
            style={{
              width: `${p.size}px`,
              height: `${p.size}px`,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${color}, rgba(255,50,0,0.3), transparent)`,
              '--tx': `${tx}vmin`,
              '--ty': `${ty}vmin`,
              animation: `fireball-particle ${p.duration}s ease-out ${p.delay}s forwards`,
              opacity: 0,
            } as React.CSSProperties}
          />
        );
      })}
    </>
  );
}

function ResurrectionEffect() {
  const sparkles = useMemo(() => generateSparkles(30), []);
  return (
    <>
      {/* Golden column */}
      <div
        className="absolute bottom-0 left-1/2"
        style={{
          width: '30vw',
          height: '100%',
          marginLeft: '-15vw',
          background: 'linear-gradient(to top, rgba(251,191,36,0.6), rgba(254,243,199,0.3) 60%, transparent 100%)',
          transformOrigin: 'bottom center',
          animation: 'resurrection-column 4.5s ease-out forwards',
        }}
      />
      {/* Side glow */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          height: '40%',
          background: 'radial-gradient(ellipse at 50% 100%, rgba(251,191,36,0.4), transparent 70%)',
          animation: 'resurrection-column 4.5s ease-out forwards',
        }}
      />
      {/* Sparkles */}
      {sparkles.map((s) => (
        <div
          key={s.id}
          className="absolute"
          style={{
            left: `${s.x}%`,
            bottom: '5%',
            width: `${s.size}px`,
            height: `${s.size}px`,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #fef3c7, #fbbf24, transparent)',
            boxShadow: '0 0 8px #fbbf24',
            '--drift': `${s.drift}px`,
            animation: `resurrection-sparkle ${s.duration}s ease-out ${s.delay}s forwards`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}
    </>
  );
}

function TransformationEffect() {
  const rings = useMemo(() => generateRings(8), []);
  const particles = useMemo(() => generateParticles(20), []);
  return (
    <>
      {/* Green pulse overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(34,197,94,0.3), transparent 70%)',
          animation: 'transformation-pulse 4s ease-in-out forwards',
        }}
      />
      {/* Concentric rings */}
      {rings.map((r) => (
        <div
          key={r.id}
          className="absolute left-1/2 top-1/2"
          style={{
            width: '30vmin',
            height: '30vmin',
            border: `${r.thickness}px solid rgba(34,197,94,0.6)`,
            borderRadius: '50%',
            '--max-scale': String(r.maxScale),
            '--rotation': `${r.rotation}deg`,
            animation: `transformation-ring 3s ease-out ${r.delay}s forwards`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}
      {/* Spinning particle ring */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: '40vmin',
          height: '40vmin',
          animation: 'transformation-spin 3s linear forwards',
        }}
      >
        {particles.map((p) => {
          const rad = (p.angle * Math.PI) / 180;
          const x = 50 + Math.cos(rad) * 45;
          const y = 50 + Math.sin(rad) * 45;
          return (
            <div
              key={p.id}
              className="absolute"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                width: `${6 + p.size * 0.15}px`,
                height: `${6 + p.size * 0.15}px`,
                borderRadius: '50%',
                background: p.hue < 0.5
                  ? 'radial-gradient(circle, #4ade80, transparent)'
                  : 'radial-gradient(circle, #22c55e, transparent)',
                boxShadow: '0 0 6px rgba(34,197,94,0.8)',
                animation: `effect-fade-in 0.5s ${p.delay}s both`,
              }}
            />
          );
        })}
      </div>
    </>
  );
}

function DivineLightEffect() {
  const rays = useMemo(() => generateRays(10), []);
  const sparkles = useMemo(() => generateSparkles(15), []);
  return (
    <>
      {/* White flash */}
      <div
        className="absolute inset-0"
        style={{
          background: 'rgba(255,255,255,1)',
          animation: 'divine-flash 3.5s ease-out forwards',
        }}
      />
      {/* Halo */}
      <div
        className="absolute left-1/2 top-0"
        style={{
          width: '50vw',
          height: '50vh',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,223,100,0.6), rgba(255,200,50,0.2), transparent 70%)',
          animation: 'divine-halo 3.5s ease-out forwards',
          opacity: 0,
        }}
      />
      {/* Light rays */}
      {rays.map((r) => (
        <div
          key={r.id}
          className="absolute"
          style={{
            left: '50%',
            top: '0',
            width: `${r.width}vw`,
            height: '120%',
            marginLeft: `${-r.width / 2}vw`,
            transformOrigin: 'top center',
            background: `linear-gradient(to bottom, rgba(255,223,100,${r.opacity}), rgba(255,255,200,${r.opacity * 0.5}) 40%, transparent 90%)`,
            '--angle': `${r.angle}deg`,
            '--ray-opacity': String(r.opacity),
            animation: `divine-ray 3.5s ease-out ${r.delay}s forwards`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}
      {/* Sparkles descending */}
      {sparkles.map((s) => (
        <div
          key={s.id}
          className="absolute"
          style={{
            left: `${s.x}%`,
            top: '10%',
            width: `${s.size}px`,
            height: `${s.size}px`,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #fff, #fde68a, transparent)',
            boxShadow: '0 0 10px rgba(255,223,100,0.8)',
            '--drift': `${s.drift}px`,
            animation: `resurrection-sparkle ${s.duration}s ease-out ${s.delay + 0.3}s reverse forwards`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}
    </>
  );
}

function SleepEffect() {
  const zzz = useMemo(() => generateZzz(24), []);
  return (
    <>
      {/* Purple mist */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 60%, rgba(147,51,234,0.35), rgba(88,28,135,0.15) 50%, transparent 80%)',
          animation: 'sleep-mist 6.5s ease-in-out forwards',
        }}
      />
      {/* Floating Z letters */}
      {zzz.map((z) => (
        <div
          key={z.id}
          className="absolute font-bold"
          style={{
            left: `${z.x}%`,
            bottom: '15%',
            fontSize: `${z.size}rem`,
            color: 'rgba(216,180,254,0.9)',
            textShadow: '0 0 20px rgba(147,51,234,0.8), 0 0 40px rgba(147,51,234,0.4)',
            fontFamily: 'serif',
            '--drift': `${z.drift}px`,
            animation: `sleep-zzz ${z.duration}s ease-out ${z.delay}s forwards`,
            opacity: 0,
          } as React.CSSProperties}
        >
          Z
        </div>
      ))}
    </>
  );
}

function MagicMissilesEffect() {
  const missiles = useMemo(() => generateMissiles(6), []);
  return (
    <>
      {/* Ambient fire tint */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 0% 50%, rgba(255,80,0,0.1), transparent 60%)',
          animation: 'effect-fade-in 0.3s ease-out forwards',
        }}
      />
      {/* Fire arrows */}
      {missiles.map((m) => {
        const w = m.size * 7;
        const h = m.size * 2.8;
        return (
          <div
            key={m.id}
            className="absolute"
            style={{
              width: `${w}px`,
              height: `${h}px`,
              '--start-y': `${m.startY}%`,
              '--curve': `${m.curve}vh`,
              animation: `missile-streak ${m.duration}s cubic-bezier(0.25, 0.1, 0.25, 1) ${m.delay}s forwards`,
              opacity: 0,
              left: '-8%',
            } as React.CSSProperties}
          >
            {/* Arrow head (pointed oval) */}
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: `${w * 0.5}px`,
                height: `${h}px`,
                borderRadius: '5% 50% 50% 5%',
                background: 'linear-gradient(to right, #ff6600, #ffaa00, #fff8 80%)',
                boxShadow: '0 0 12px #ff6600, 0 0 25px rgba(255,100,0,0.6)',
              }}
            />
            {/* Fire trail */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: '50%',
                width: `${w * 0.7}px`,
                height: `${h * 1.2}px`,
                borderRadius: '40% 10% 10% 40%',
                background: 'linear-gradient(to right, transparent 5%, rgba(255,50,0,0.3) 20%, rgba(255,100,0,0.6) 50%, #ff6600 90%)',
                filter: 'blur(3px)',
                animation: `missile-flame-flicker 0.15s ease-in-out infinite`,
              }}
            />
            {/* Inner bright core */}
            <div
              style={{
                position: 'absolute',
                right: `${w * 0.1}px`,
                top: '50%',
                transform: 'translateY(-50%)',
                width: `${w * 0.25}px`,
                height: `${h * 0.5}px`,
                borderRadius: '20% 50% 50% 20%',
                background: 'linear-gradient(to right, #ffcc00, #fff)',
                boxShadow: '0 0 8px #ffcc00',
              }}
            />
            {/* Ember particles */}
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${10 + i * 15}%`,
                  top: `${30 + (i % 2) * 40}%`,
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: '#ffaa00',
                  boxShadow: '0 0 4px #ff6600',
                  opacity: 0.7 - i * 0.2,
                }}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}

function PoisonEffect() {
  const bubbles = useMemo(() => generateBubbles(25), []);
  const drips = useMemo(() => generateDrips(12), []);
  return (
    <>
      {/* Toxic green mist */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 70%, rgba(34,197,94,0.3), rgba(22,163,74,0.15) 40%, rgba(5,46,22,0.1) 70%, transparent 90%)',
          animation: 'poison-mist 5s ease-in-out forwards',
        }}
      />
      {/* Drips from top */}
      {drips.map((d) => (
        <div
          key={d.id}
          className="absolute top-0"
          style={{
            left: `${d.x}%`,
            width: `${d.width}px`,
            borderRadius: '0 0 50% 50%',
            background: 'linear-gradient(to bottom, rgba(34,197,94,0.7), rgba(22,163,74,0.3))',
            boxShadow: '0 2px 8px rgba(34,197,94,0.4)',
            '--drip-h': `${d.height}vh`,
            animation: `poison-drip 4s ease-in ${d.delay}s forwards`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}
      {/* Rising bubbles */}
      {bubbles.map((b) => {
        const color = b.hue < 0.4 ? 'rgba(34,197,94,0.7)' : b.hue < 0.7 ? 'rgba(74,222,128,0.6)' : 'rgba(22,163,74,0.8)';
        return (
          <div
            key={b.id}
            className="absolute"
            style={{
              left: `${b.x}%`,
              bottom: '5%',
              width: `${b.size}px`,
              height: `${b.size}px`,
              borderRadius: '50%',
              background: `radial-gradient(circle at 35% 35%, rgba(255,255,255,0.2), ${color}, transparent)`,
              border: '1px solid rgba(34,197,94,0.3)',
              '--drift': `${b.drift}px`,
              animation: `poison-bubble ${b.duration}s ease-out ${b.delay}s forwards`,
              opacity: 0,
            } as React.CSSProperties}
          />
        );
      })}
    </>
  );
}

function DruidSummonEffect() {
  const runes = useMemo(() => ['᛭', 'ᚠ', 'ᚢ', 'ᚦ', 'ᚨ', 'ᚱ', 'ᚲ', 'ᛇ'], []);
  const leaves = useMemo(() => Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: 20 + Math.random() * 60,
    y: 30 + Math.random() * 40,
    lx1: -30 + Math.random() * 60,
    ly1: -20 + Math.random() * -30,
    lx2: -50 + Math.random() * 100,
    ly2: -40 + Math.random() * -40,
    lx3: -70 + Math.random() * 140,
    ly3: -50 + Math.random() * -50,
    delay: Math.random() * 2,
    duration: 2.5 + Math.random() * 2,
    size: 10 + Math.random() * 18,
    hue: Math.random(),
  })), []);

  return (
    <>
      {/* Earth glow from below */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 80%, rgba(101,67,33,0.4), rgba(34,120,60,0.2) 40%, transparent 70%)',
          animation: 'poison-mist 5s ease-in-out forwards',
        }}
      />
      {/* Summoning circle */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: '40vmin',
          height: '40vmin',
          borderRadius: '50%',
          border: '4px solid rgba(34,197,94,0.6)',
          boxShadow: '0 0 30px rgba(34,197,94,0.4), inset 0 0 30px rgba(34,197,94,0.2)',
          animation: 'summon-circle 5s ease-in-out forwards',
        }}
      >
        {/* Inner circle */}
        <div
          style={{
            position: 'absolute',
            inset: '15%',
            borderRadius: '50%',
            border: '2px solid rgba(180,140,60,0.5)',
            boxShadow: '0 0 15px rgba(180,140,60,0.3)',
          }}
        />
      </div>
      {/* Pillar of light */}
      <div
        className="absolute left-1/2 bottom-0"
        style={{
          width: '20vw',
          height: '100%',
          marginLeft: '-10vw',
          background: 'linear-gradient(to top, rgba(101,67,33,0.5), rgba(34,197,94,0.3) 40%, rgba(180,140,60,0.2) 70%, transparent)',
          transformOrigin: 'bottom center',
          animation: 'summon-pillar 5s ease-in-out forwards',
        }}
      />
      {/* Floating runes */}
      {runes.map((rune, i) => {
        const angle = (360 / runes.length) * i;
        const rad = (angle * Math.PI) / 180;
        const rx = Math.cos(rad) * 22;
        const ry = Math.sin(rad) * 22;
        return (
          <div
            key={i}
            className="absolute left-1/2 top-1/2 font-bold"
            style={{
              fontSize: '3rem',
              color: 'rgba(180,140,60,0.9)',
              textShadow: '0 0 15px rgba(34,197,94,0.8), 0 0 30px rgba(180,140,60,0.5)',
              '--rx': `${rx}vmin`,
              '--ry': `${ry}vmin`,
              animation: `summon-rune 4s ease-in-out ${i * 0.2}s forwards`,
              opacity: 0,
            } as React.CSSProperties}
          >
            {rune}
          </div>
        );
      })}
      {/* Swirling leaves / nature particles */}
      {leaves.map((l) => (
        <div
          key={l.id}
          className="absolute"
          style={{
            left: `${l.x}%`,
            top: `${l.y}%`,
            width: `${l.size}px`,
            height: `${l.size * 0.6}px`,
            borderRadius: '50% 0 50% 0',
            background: l.hue < 0.5
              ? 'linear-gradient(135deg, #22c55e, #16a34a)'
              : 'linear-gradient(135deg, #65a30d, #4d7c0f)',
            boxShadow: '0 0 4px rgba(34,197,94,0.5)',
            '--lx1': `${l.lx1}px`,
            '--ly1': `${l.ly1}px`,
            '--lx2': `${l.lx2}px`,
            '--ly2': `${l.ly2}px`,
            '--lx3': `${l.lx3}px`,
            '--ly3': `${l.ly3}px`,
            animation: `summon-leaf ${l.duration}s ease-in-out ${l.delay}s forwards`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}
    </>
  );
}

function BardEffect() {
  const notes = useMemo(() => generateNotes(20), []);
  return (
    <>
      {/* Warm golden glow */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 60%, rgba(251,191,36,0.25), rgba(217,119,6,0.1) 50%, transparent 80%)',
          animation: 'bard-glow 6s ease-in-out forwards',
        }}
      />
      {/* Floating musical notes */}
      {notes.map((n) => {
        const color = n.hue < 0.33
          ? 'rgba(251,191,36,0.9)'
          : n.hue < 0.66
            ? 'rgba(253,224,71,0.9)'
            : 'rgba(245,158,11,0.9)';
        return (
          <div
            key={n.id}
            className="absolute font-bold"
            style={{
              left: `${n.x}%`,
              bottom: '10%',
              fontSize: `${n.size}rem`,
              color,
              textShadow: '0 0 15px rgba(251,191,36,0.7), 0 0 30px rgba(217,119,6,0.4)',
              '--drift': `${n.drift}px`,
              animation: `bard-note ${n.duration}s ease-out ${n.delay}s forwards`,
              opacity: 0,
            } as React.CSSProperties}
          >
            {n.symbol}
          </div>
        );
      })}
    </>
  );
}

function BattleCryEffect() {
  const shockwaves = useMemo(() => generateShockwaves(5), []);
  const lines = useMemo(() => generateForceLines(16), []);
  return (
    <>
      {/* Initial flash */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(239,68,68,0.6), rgba(220,38,38,0.2) 40%, transparent 70%)',
          animation: 'battlecry-flash 3s ease-out forwards',
        }}
      />
      {/* Shockwave rings */}
      {shockwaves.map((sw) => (
        <div
          key={sw.id}
          className="absolute left-1/2 top-1/2"
          style={{
            width: '30vmin',
            height: '30vmin',
            borderRadius: '50%',
            border: '6px solid rgba(239,68,68,0.8)',
            '--max-scale': String(sw.maxScale),
            animation: `battlecry-ring ${sw.duration}s ease-out ${sw.delay}s forwards`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}
      {/* Force lines radiating from center */}
      {lines.map((l) => (
        <div
          key={l.id}
          className="absolute"
          style={{
            left: '50%',
            top: '50%',
            width: '55vmax',
            height: `${l.width}px`,
            transformOrigin: 'left center',
            background: `linear-gradient(to right, rgba(239,68,68,${l.opacity}), rgba(252,165,165,${l.opacity * 0.5}) 60%, transparent 100%)`,
            '--angle': `${l.angle}deg`,
            '--line-opacity': String(l.opacity),
            animation: `battlecry-line 2.5s ease-out ${l.delay}s forwards`,
            opacity: 0,
          } as React.CSSProperties}
        />
      ))}
    </>
  );
}

const EFFECT_RENDERERS: Record<BattleEffectType, () => JSX.Element> = {
  fireball: FireballEffect,
  resurrection: ResurrectionEffect,
  transformation: TransformationEffect,
  'divine-light': DivineLightEffect,
  sleep: SleepEffect,
  'magic-missiles': MagicMissilesEffect,
  poison: PoisonEffect,
  'battle-cry': BattleCryEffect,
  'druid-summon': DruidSummonEffect,
  bard: BardEffect,
};

export function BattleEffectsOverlay({ socket }: BattleEffectsOverlayProps) {
  const [currentEffect, setCurrentEffect] = useState<BattleEffectType | null>(null);
  // key to force remount on repeated same effect
  const [effectKey, setEffectKey] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!socket) return;

    const handleEffect = (data: { effectType: BattleEffectType; timestamp: number }) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      setCurrentEffect(data.effectType);
      setEffectKey((k) => k + 1);

      const duration = EFFECT_DURATIONS[data.effectType] ?? 4000;
      timeoutRef.current = setTimeout(() => {
        setCurrentEffect(null);
      }, duration);
    };

    socket.on('display:battle-effect', handleEffect);
    return () => {
      socket.off('display:battle-effect', handleEffect);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [socket]);

  if (!currentEffect) return null;

  const EffectComponent = EFFECT_RENDERERS[currentEffect];

  return (
    <div key={effectKey} className="absolute inset-0 z-[60] pointer-events-none overflow-hidden">
      <style>{CSS_KEYFRAMES}</style>
      <EffectComponent />
    </div>
  );
}
